import { BlockId } from '../world/BlockType.js';
import { MAX_STACK_SIZE, HOTBAR_SIZE, STORAGE_SIZE } from '../constants.js';

export class Inventory {
  constructor() {
    // Hotbar: HOTBAR_SIZE slots (always visible at bottom)
    this.slots = new Array(HOTBAR_SIZE).fill(null);
    // Storage: STORAGE_SIZE slots (visible when inventory screen is open)
    this.storage = new Array(STORAGE_SIZE).fill(null);
    this.selectedIndex = 0;

    // Default starter blocks (will be cleared in Survival mode)
    this.slots[0] = { blockId: BlockId.DIRT, count: MAX_STACK_SIZE };
    this.slots[1] = { blockId: BlockId.GRASS, count: MAX_STACK_SIZE };
    this.slots[2] = { blockId: BlockId.STONE, count: MAX_STACK_SIZE };
    this.slots[3] = { blockId: BlockId.SAND, count: MAX_STACK_SIZE };
    this.slots[4] = { blockId: BlockId.WOOD, count: MAX_STACK_SIZE };
    this.slots[5] = { blockId: BlockId.LEAVES, count: 32 };
    this.slots[6] = { blockId: BlockId.PLANKS, count: MAX_STACK_SIZE };
    this.slots[7] = { blockId: BlockId.COBBLESTONE, count: MAX_STACK_SIZE };
    this.slots[8] = { blockId: BlockId.GLASS, count: 32 };

    // Some items in storage
    this.storage[0] = { blockId: BlockId.DOOR_CLOSED, count: 16 };
  }

  /**
   * Clear all inventory slots (for Survival mode start).
   */
  clearAll() {
    this.slots.fill(null);
    this.storage.fill(null);
    this.selectedIndex = 0;
  }

  getSelectedBlock() {
    const slot = this.slots[this.selectedIndex];
    if (slot && slot.count > 0 && slot.blockId !== undefined) return slot.blockId;
    return null;
  }

  getSelectedSlot() {
    return this.slots[this.selectedIndex];
  }

  consumeSelected() {
    const slot = this.slots[this.selectedIndex];
    if (slot && slot.count > 0) {
      slot.count--;
      if (slot.count <= 0) {
        this.slots[this.selectedIndex] = null;
      }
      return true;
    }
    return false;
  }

  addBlock(blockId) {
    if (blockId === BlockId.BEDROCK || blockId === BlockId.WATER) return false;
    // Normalize open doors to closed for inventory
    if (blockId === BlockId.DOOR_OPEN) blockId = BlockId.DOOR_CLOSED;

    // Try stacking in hotbar first
    for (let i = 0; i < this.slots.length; i++) {
      if (this.slots[i] && this.slots[i].blockId === blockId && this.slots[i].count < MAX_STACK_SIZE) {
        this.slots[i].count++;
        return true;
      }
    }
    // Try stacking in storage
    for (let i = 0; i < this.storage.length; i++) {
      if (this.storage[i] && this.storage[i].blockId === blockId && this.storage[i].count < MAX_STACK_SIZE) {
        this.storage[i].count++;
        return true;
      }
    }
    // Empty slot in hotbar
    for (let i = 0; i < this.slots.length; i++) {
      if (!this.slots[i]) {
        this.slots[i] = { blockId, count: 1 };
        return true;
      }
    }
    // Empty slot in storage
    for (let i = 0; i < this.storage.length; i++) {
      if (!this.storage[i]) {
        this.storage[i] = { blockId, count: 1 };
        return true;
      }
    }
    return false;
  }

  // Add a non-block item (negative itemId) to inventory
  addItem(itemId, count = 1) {
    if (count <= 0) return false;
    // Try stacking in hotbar
    for (let i = 0; i < this.slots.length; i++) {
      if (this.slots[i] && this.slots[i].itemId === itemId && this.slots[i].count < MAX_STACK_SIZE) {
        const canAdd = Math.min(count, MAX_STACK_SIZE - this.slots[i].count);
        this.slots[i].count += canAdd;
        count -= canAdd;
        if (count <= 0) return true;
      }
    }
    // Try stacking in storage
    for (let i = 0; i < this.storage.length; i++) {
      if (this.storage[i] && this.storage[i].itemId === itemId && this.storage[i].count < MAX_STACK_SIZE) {
        const canAdd = Math.min(count, MAX_STACK_SIZE - this.storage[i].count);
        this.storage[i].count += canAdd;
        count -= canAdd;
        if (count <= 0) return true;
      }
    }
    // Empty slot in hotbar
    for (let i = 0; i < this.slots.length; i++) {
      if (!this.slots[i]) {
        const place = Math.min(count, MAX_STACK_SIZE);
        this.slots[i] = { itemId, count: place };
        count -= place;
        if (count <= 0) return true;
      }
    }
    // Empty slot in storage
    for (let i = 0; i < this.storage.length; i++) {
      if (!this.storage[i]) {
        const place = Math.min(count, MAX_STACK_SIZE);
        this.storage[i] = { itemId, count: place };
        count -= place;
        if (count <= 0) return true;
      }
    }
    // Returns false if not all items could be placed (inventory full)
    return false;
  }

  // Add a tool item to the first empty slot (tools don't stack)
  addTool(tool) {
    // Try hotbar first
    for (let i = 0; i < this.slots.length; i++) {
      if (!this.slots[i]) {
        this.slots[i] = tool;
        return true;
      }
    }
    // Try storage
    for (let i = 0; i < this.storage.length; i++) {
      if (!this.storage[i]) {
        this.storage[i] = tool;
        return true;
      }
    }
    return false; // inventory full
  }

  // Unified slot access (0 to HOTBAR_SIZE-1 = hotbar, HOTBAR_SIZE+ = storage)
  getUnifiedSlot(idx) {
    if (idx < HOTBAR_SIZE) return this.slots[idx];
    return this.storage[idx - HOTBAR_SIZE];
  }

  setUnifiedSlot(idx, item) {
    if (idx < HOTBAR_SIZE) {
      this.slots[idx] = item;
    } else {
      this.storage[idx - HOTBAR_SIZE] = item;
    }
  }

  // Get item from any area: 'hotbar' slots[index] or 'storage' storage[index]
  getItem(area, index) {
    return area === 'hotbar' ? this.slots[index] : this.storage[index];
  }

  setItem(area, index, item) {
    if (area === 'hotbar') {
      this.slots[index] = item;
    } else {
      this.storage[index] = item;
    }
  }

  // Returns true if the inventory contains at least one of the given item.
  // Accepts blockId (positive) via slot.blockId or itemId (negative) via slot.itemId.
  hasItem(itemId) {
    const check = (slot) => slot && (slot.itemId === itemId || slot.blockId === itemId);
    for (const s of this.slots) if (check(s)) return true;
    for (const s of this.storage) if (check(s)) return true;
    return false;
  }

  selectSlot(index) {
    if (index >= 0 && index < this.slots.length) {
      this.selectedIndex = index;
    }
  }

  scrollSelection(delta) {
    this.selectedIndex = ((this.selectedIndex + delta) % this.slots.length + this.slots.length) % this.slots.length;
  }

  serialize() {
    return { slots: this.slots, storage: this.storage, selectedIndex: this.selectedIndex };
  }

  deserialize(data) {
    if (data && data.slots) {
      this.slots = data.slots;
      this.selectedIndex = data.selectedIndex || 0;
    }
    if (data && data.storage) {
      this.storage = data.storage;
    }
  }
}
